import React, { createContext, useState, useCallback, useContext } from "react";
import { FailureDetailsSettingsContext } from "contexts/FailureDetailsSettings/FailureDetailsSettingsContext";
import {
    ISelectedAreaToDelete,
    ISelectedAreaToEdit,
    ISelectedFunctionalLocationToDelete,
    ISelectedFunctionalLocationToEdit,
} from "../AreasListInterfaces";
import { IArea, IFunctionalLocation } from "entities/FailureDetailsEntity/FailureDetailsEntityInterfaces";
import { handleNameTranslation } from "utilities/localesUtilities";
import { useTranslation } from "react-i18next";
import { NetworkStatus } from "@apollo/client";
import { useParams } from "react-router-dom";
import { FailureDetailsEntity } from "entities/FailureDetailsEntity/FailureDetailsEntity";
import { IAreasListContext, IAreasListContextProps } from "./interfaces";

const initialState: IAreasListContext = {
    areas: [],
    areaTabs: [],
    openAreaModal: false,
    openFunctionalLocationModal: false,
    selectedAreaToEdit: null,
    selectedFunctionalLocationToEdit: null,
    selectedAreaToDelete: null,
    selectedFunctionalLocationToDelete: null,
    refetch: async () => {
        return { data: [], loading: false, networkStatus: NetworkStatus.ready };
    },
    setAreas: () => {},
    toggleAreaTab: () => {},
    toggleFunctionalLocationTab: () => {},
    handleOpenAreaModal: () => {},
    handleCloseAreaModal: () => {},
    handleOpenFunctionalLocationModal: () => {},
    handleCloseFunctionalLocationModal: () => {},
    setAreaToEdit: () => {},
    setFunctionalLocationToEdit: () => {},
    setAreaToDelete: () => {},
    setFunctionalLocationToDelete: () => {},
    handleCloseDeletionModal: () => {},
};

export const AreasListContext = createContext<IAreasListContext>(initialState);

export const AreasListContextProvider: React.FC<IAreasListContextProps> = ({ children }) => {
    const { machineCenterId } = useParams();

    const { handleSelectedAreaId, handleSelectedFunctionalLocationId, selectedFunctionalLocationId } =
        useContext(FailureDetailsSettingsContext);

    const { areas, refetch, setAreas } = FailureDetailsEntity.GetAreas(+machineCenterId!);
    const { refetch: failureCodesIdsRefetch } =
        FailureDetailsEntity.GetLocationSpecificFailureCodesByFunctionalLocationId(selectedFunctionalLocationId!);

    const { i18n } = useTranslation();

    const [areaTabs, setAreaTabs] = useState<number[]>([]);
    const [openAreaModal, setOpenAreaModal] = useState(false);
    const [openFunctionalLocationModal, setOpenFunctionalLocationModal] = useState(false);
    const [selectedAreaToEdit, setSelectedAreaToEdit] = useState<ISelectedAreaToEdit | null>(null);
    const [selectedFunctionalLocationToEdit, setSelectedFunctionalLocationToEdit] =
        useState<ISelectedFunctionalLocationToEdit | null>(null);
    const [selectedAreaToDelete, setSelectedAreaToDelete] = useState<ISelectedAreaToDelete | null>(null);
    const [selectedFunctionalLocationToDelete, setSelectedFunctionalLocationToDelete] =
        useState<ISelectedFunctionalLocationToDelete | null>(null);

    const toggleAreaTab = useCallback(
        (id: number, isDragging?: boolean) => {
            if (!isDragging) {
                handleSelectedAreaId(id);
                setAreaTabs((prev) => {
                    if (prev.includes(id)) {
                        return prev.filter((item) => item !== id);
                    } else {
                        return [...prev, id];
                    }
                });
            }
        },
        [handleSelectedAreaId],
    );

    const toggleFunctionalLocationTab = useCallback(
        (id: number, isDragging?: boolean) => {
            if (!isDragging) {
                handleSelectedFunctionalLocationId(id);
                failureCodesIdsRefetch!();
            }
        },
        [handleSelectedFunctionalLocationId, failureCodesIdsRefetch],
    );

    const handleOpenAreaModal = useCallback(() => {
        setOpenAreaModal(true);
    }, []);

    const handleCloseAreaModal = useCallback(() => {
        refetch();
        setOpenAreaModal(false);
        setSelectedAreaToEdit(null);
        handleSelectedAreaId(null);
    }, [handleSelectedAreaId, refetch]);

    const handleOpenFunctionalLocationModal = useCallback(
        (area_id: number, functional_location_id: number | null) => {
            setOpenFunctionalLocationModal(true);
            handleSelectedAreaId(area_id);
            handleSelectedFunctionalLocationId(functional_location_id);
        },
        [handleSelectedAreaId, handleSelectedFunctionalLocationId],
    );

    const handleCloseFunctionalLocationModal = useCallback(() => {
        refetch();
        setOpenFunctionalLocationModal(false);
        setSelectedFunctionalLocationToEdit(null);
        handleSelectedFunctionalLocationId(null);
    }, [handleSelectedFunctionalLocationId, refetch]);

    const handleCloseDeletionModal = useCallback(() => {
        refetch();
        setSelectedAreaToDelete(null);
        setSelectedFunctionalLocationToDelete(null);
        handleSelectedFunctionalLocationId(null);
        handleSelectedAreaId(null);
    }, [refetch, handleSelectedAreaId, handleSelectedFunctionalLocationId]);

    const setAreaToEdit = useCallback((area: IArea) => {
        setSelectedAreaToEdit({
            area_id: area.area_id,
            translation: area.translation,
        });
    }, []);

    const setFunctionalLocationToEdit = useCallback(
        (functionalLocation: IFunctionalLocation, areaId: number) => {
            setSelectedFunctionalLocationToEdit({
                functional_location_id: functionalLocation.functional_location_id!,
                translation: functionalLocation.translation,
            });
            handleSelectedAreaId(areaId);
        },
        [handleSelectedAreaId],
    );

    const setAreaToDelete = useCallback(
        (area: IArea) => {
            setSelectedAreaToDelete({
                area_id: area.area_id,
                name: handleNameTranslation(i18n.language, area.translation.translation_names),
            });
        },
        [i18n.language],
    );

    const setFunctionalLocationToDelete = useCallback(
        (functionalLocation: IFunctionalLocation) => {
            setSelectedFunctionalLocationToDelete({
                functional_location_id: functionalLocation.functional_location_id!,
                name: handleNameTranslation(i18n.language, functionalLocation.translation.translation_names),
            });
        },
        [i18n.language],
    );

    const providerValue: IAreasListContext = {
        areas,
        areaTabs,
        openAreaModal,
        openFunctionalLocationModal,
        selectedAreaToEdit,
        selectedFunctionalLocationToEdit,
        selectedAreaToDelete,
        selectedFunctionalLocationToDelete,
        refetch,
        setAreas,
        toggleAreaTab,
        toggleFunctionalLocationTab,
        handleOpenAreaModal,
        handleCloseAreaModal,
        handleOpenFunctionalLocationModal,
        handleCloseFunctionalLocationModal,
        setAreaToEdit,
        setFunctionalLocationToEdit,
        setAreaToDelete,
        setFunctionalLocationToDelete,
        handleCloseDeletionModal,
    };

    return <AreasListContext.Provider value={providerValue}>{children}</AreasListContext.Provider>;
};

export const useAreasListContext = () => {
    const context = useContext(AreasListContext);
    if (!context) {
        throw new Error("useAreasListContext must be used within AreasListContextProvider");
    }
    return context;
};
